/**
 * Leave management API — Mon Espace → Congés.
 * Backend at /api/leave/*.
 */

import { apiClient } from '@/core/api/client';

// ---------- Types ----------

export type LeaveType = 'legal' | 'recup' | 'unpaid' | 'sick';
export type LeaveStatus = 'pending' | 'approved' | 'denied' | 'cancelled';
export type HalfDayPart = 'full' | 'am' | 'pm';

export interface LeaveRequest {
  id: string;
  user_id: string;
  date_from: string;        // YYYY-MM-DD
  date_to: string;          // YYYY-MM-DD
  half_day_start: HalfDayPart;
  half_day_end: HalfDayPart;
  type: LeaveType;
  status: LeaveStatus;
  reason: string | null;
  decision_by: string | null;
  decision_at: string | null;
  decision_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeaveRequestEnriched extends LeaveRequest {
  first_name?: string;
  last_name?: string;
  user_email?: string;
  user_role?: string;
  decision_first_name?: string | null;
  decision_last_name?: string | null;
}

export type EmploymentType = 'employee' | 'independent';

export interface LeaveBalance {
  year: number;
  employment_type: EmploymentType;
  legal_quota: number;
  legal_used: number;
  legal_remaining: number;
  recup_used: number;
  recup_hours_balance: number;
  recup_tracking_start: string;   // YYYY-MM-DD
  base_hours_per_week: number;
  total_validated_hours: number;  // sum since recup_tracking_start
  pending_count: number;
}

export interface PublicHoliday {
  id: number;
  date: string;             // YYYY-MM-DD
  label: string;
  is_legal: boolean;
}

export interface UserLeaveConfig {
  user_id: string;
  year: number;
  legal_leave_days: number;
  base_hours_per_week: number;
  recup_tracking_start?: string;  // YYYY-MM-DD, fallback at code level if absent
  employment_type?: EmploymentType;
  working_days?: string;          // CSV of weekday names, e.g. 'monday,tuesday,...'; default Mon–Fri
  _default?: boolean;
}

export interface UserLeaveConfigEntry extends UserLeaveConfig {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  has_config: boolean;
}

export interface ApprovedLeaveDay {
  date: string;             // YYYY-MM-DD
  fraction: number;         // 1 or 0.5
  type: LeaveType;
  request_id: string;
}

export interface TeamDayHours {
  user_id: string;
  scheduled_hours: number;
}

export interface UnvalidatedWeek {
  week_start: string;       // YYYY-MM-DD (Monday)
  planned_hours: number;    // live preview of what would be locked in
}

export interface ValidatedWeek {
  week_start: string;
  planned_hours: string;    // pg returns numeric as string
  validated_at: string;
}

export interface CreateLeaveRequestPayload {
  dateFrom: string;
  dateTo: string;
  halfDayStart?: HalfDayPart;
  halfDayEnd?: HalfDayPart;
  type: LeaveType;
  reason?: string;
}

// ---------- API surface ----------

interface ApiResp<T> { success: boolean; data: T; message?: string }

export const leaveAPI = {
  // Holidays
  listHolidays: async (params: { from?: string; to?: string } = {}): Promise<PublicHoliday[]> => {
    const r = await apiClient.get<ApiResp<PublicHoliday[]>>('/api/leave/holidays', { params });
    return r.data;
  },

  // Config
  getMyConfig: async (year?: number): Promise<UserLeaveConfig> => {
    const r = await apiClient.get<ApiResp<UserLeaveConfig>>('/api/leave/config/me', {
      params: year ? { year } : undefined,
    });
    return r.data;
  },
  listConfigs: async (year?: number): Promise<UserLeaveConfigEntry[]> => {
    const r = await apiClient.get<ApiResp<UserLeaveConfigEntry[]>>('/api/leave/configs', {
      params: year ? { year } : undefined,
    });
    return r.data;
  },
  upsertConfig: async (userId: string, payload: { year?: number; legalLeaveDays?: number; baseHoursPerWeek?: number; recupTrackingStart?: string; employmentType?: EmploymentType; workingDays?: string }) => {
    const r = await apiClient.put<ApiResp<UserLeaveConfig>>(`/api/leave/configs/${userId}`, payload);
    return r.data;
  },

  // Requests
  listMine: async (year?: number): Promise<LeaveRequest[]> => {
    const r = await apiClient.get<ApiResp<LeaveRequest[]>>('/api/leave/requests/me', {
      params: year ? { year } : undefined,
    });
    return r.data;
  },
  listAll: async (params: { status?: LeaveStatus; userId?: string; year?: number } = {}): Promise<LeaveRequestEnriched[]> => {
    const r = await apiClient.get<ApiResp<LeaveRequestEnriched[]>>('/api/leave/requests', { params });
    return r.data;
  },
  create: async (payload: CreateLeaveRequestPayload): Promise<LeaveRequest> => {
    const r = await apiClient.post<ApiResp<LeaveRequest>>('/api/leave/requests', payload);
    return r.data;
  },
  cancel: async (id: string): Promise<LeaveRequest> => {
    const r = await apiClient.post<ApiResp<LeaveRequest>>(`/api/leave/requests/${id}/cancel`);
    return r.data;
  },
  decide: async (id: string, payload: { status: 'approved' | 'denied'; reason?: string }): Promise<LeaveRequest> => {
    const r = await apiClient.post<ApiResp<LeaveRequest>>(`/api/leave/requests/${id}/decide`, payload);
    return r.data;
  },

  // Balance
  myBalance: async (year?: number): Promise<LeaveBalance> => {
    const r = await apiClient.get<ApiResp<LeaveBalance>>('/api/leave/balance/me', {
      params: year ? { year } : undefined,
    });
    return r.data;
  },
  balanceFor: async (userId: string, year?: number): Promise<LeaveBalance> => {
    const r = await apiClient.get<ApiResp<LeaveBalance>>(`/api/leave/balance/${userId}`, {
      params: year ? { year } : undefined,
    });
    return r.data;
  },

  // Approved days (for calendar grey-out)
  myApprovedDays: async (params: { from?: string; to?: string } = {}): Promise<ApprovedLeaveDay[]> => {
    const r = await apiClient.get<ApiResp<ApprovedLeaveDay[]>>('/api/leave/approved-days/me', { params });
    return r.data;
  },

  // Team day hours — admin-only, for the day-detail drawer
  teamDayHours: async (date: string): Promise<TeamDayHours[]> => {
    const r = await apiClient.get<ApiResp<TeamDayHours[]>>('/api/leave/team/day-hours', { params: { date } });
    return r.data;
  },

  // Weekly validations
  myUnvalidatedWeeks: async (year?: number): Promise<UnvalidatedWeek[]> => {
    const r = await apiClient.get<ApiResp<UnvalidatedWeek[]>>('/api/leave/weeks/unvalidated/me', {
      params: year ? { year } : undefined,
    });
    return r.data;
  },
  myValidatedWeeks: async (year?: number): Promise<ValidatedWeek[]> => {
    const r = await apiClient.get<ApiResp<ValidatedWeek[]>>('/api/leave/weeks/validated/me', {
      params: year ? { year } : undefined,
    });
    return r.data;
  },
  validateWeek: async (weekStart: string): Promise<ValidatedWeek> => {
    const r = await apiClient.post<ApiResp<ValidatedWeek>>('/api/leave/weeks/validate', { weekStart });
    return r.data;
  },
  unvalidateWeek: async (weekStart: string): Promise<void> => {
    await apiClient.delete(`/api/leave/weeks/validate/${weekStart}`);
  },
};
